import {
  AUTH_METHODS,
  CLIENT_IDS,
  DISTRIBUTIONS,
  type Selections
} from './guide';

const ENTRY_PATHS = ['manual', 'plugin'] as const;

const ORDER = {
  manual: ['entry', 'auth', 'distribution', 'client', 'guide'],
  plugin: ['entry', 'plugin']
} as const satisfies Record<(typeof ENTRY_PATHS)[number], readonly string[]>;

export type Screen = (typeof ORDER)[keyof typeof ORDER][number];
export type EntryPath = (typeof ENTRY_PATHS)[number];

export interface FlowState {
  screen: Screen;
  path: EntryPath;
  selections: Selections;
}

type SelectAction = {
  [K in keyof Selections]: { type: 'select'; field: K; value: Selections[K] };
}[keyof Selections];

export type FlowAction =
  | { type: 'selectPath'; path: EntryPath }
  | SelectAction
  | { type: 'next' }
  | { type: 'back' }
  | { type: 'reset' };

export const initialFlowState: FlowState = {
  screen: 'entry',
  path: 'manual',
  selections: { auth: 'apiKey', distribution: 'npm', client: 'vscode' }
};

const orderFor = (path: EntryPath): readonly Screen[] => ORDER[path];

const moveScreen = (state: FlowState, offset: 1 | -1): Screen => {
  const order = orderFor(state.path);
  const index = order.indexOf(state.screen);
  if (index === -1) {
    return state.screen;
  }
  return order[index + offset] ?? state.screen;
};

const applySelect = <K extends keyof Selections>(
  selections: Selections,
  field: K,
  value: Selections[K]
): Selections => ({ ...selections, [field]: value });

export const flowReducer = (
  state: FlowState,
  action: FlowAction
): FlowState => {
  switch (action.type) {
    case 'selectPath':
      return { ...state, path: action.path };
    case 'select':
      return {
        ...state,
        selections: applySelect(state.selections, action.field, action.value)
      };
    case 'next':
      return { ...state, screen: moveScreen(state, 1) };
    case 'back':
      return { ...state, screen: moveScreen(state, -1) };
    case 'reset':
      return initialFlowState;
    default:
      return state;
  }
};

const isOneOf = <T extends string>(
  values: readonly T[],
  value: unknown
): value is T => values.includes(value as T);

/** A saved flow that no longer matches the current screens or options, eg from an older build, restarts the flow. */
export const parseFlowState = (raw: string): FlowState => {
  const parsed: unknown = JSON.parse(raw);
  if (typeof parsed !== 'object' || parsed === null) {
    return initialFlowState;
  }
  const { screen, path, selections } = parsed as Partial<
    Record<keyof FlowState, unknown>
  >;
  if (
    !isOneOf(ENTRY_PATHS, path) ||
    !isOneOf(orderFor(path), screen) ||
    typeof selections !== 'object' ||
    selections === null
  ) {
    return initialFlowState;
  }
  const { auth, distribution, client } = selections as Partial<
    Record<keyof Selections, unknown>
  >;
  if (
    !isOneOf(AUTH_METHODS, auth) ||
    !isOneOf(DISTRIBUTIONS, distribution) ||
    !isOneOf(CLIENT_IDS, client)
  ) {
    return initialFlowState;
  }
  return { screen, path, selections: { auth, distribution, client } };
};
